/**
 * Revenues — analyse du chiffre d'affaires facturé par employé / pool, par année,
 * avec comparaison vs prior year et bridge price / volume / mix.
 *
 *  - Montant facturé = saisie réelle (chr_yearly_inputs.billed_amount), éditable ici.
 *  - Tarif effectif = billed_amount ÷ heures billables (heures_tf).
 *  - Bridge : Revenue PY → Volume → Price → Mix → Scope → Revenue CY
 *      Volume = (ΣH_cy − ΣH_py) × tarif_moyen_py
 *      Price  = Σ H_cy_i × (tarif_cy_i − tarif_py_i)
 *      Mix    = Σ [(part_cy_i − part_py_i) × ΣH_cy] × (tarif_py_i − tarif_moyen_py)
 *      Scope  = Σ arrivées (R_cy) − Σ départs (R_py)
 *    Décliné par pool ET par employé (mêmes totaux, deux lectures).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useP } from '../../i18n/useP';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { getChrYearly, upsertChrYearlyInput } from '../../api/chrYearly';
import { listCostPools } from '../../api/tdabc/cost_pools';
import { formatNumber } from '../../utils/format';
import PageHeader from '../../components/PageHeader';
import './RevenuesPage.css';

const UNASSIGNED = '__UNASSIGNED__';

const chf  = (v) => (v == null ? '—' : formatNumber(v, 0));
const rate = (v) => (v == null ? '—' : formatNumber(v, 2));
const fmtFte = (v) => (v == null ? '—' : formatNumber(v, 1));
function pct(v) {
  if (v == null || !isFinite(v)) return '—';
  return `${(v * 100).toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}
const sign = (v) => (v > 0 ? 'rev-pos' : v < 0 ? 'rev-neg' : '');
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${formatNumber(v, 0)}`);

// ── Tri générique : sort = {key, dir}, accessors = { key: row => valeur } ───────
// Chaînes triées via localeCompare 'fr' (accents) ; valeurs nulles toujours en bas.
function sortRows(rows, sort, accessors) {
  const acc = accessors[sort.key];
  if (!acc) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = acc(a), vb = acc(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'string' || typeof vb === 'string') {
      return dir * String(va).localeCompare(String(vb), 'fr', { numeric: true });
    }
    return dir * (va - vb);
  });
}
// Fabrique un handler de tri pour un setState donné (toggle asc/desc, sinon defDir).
const makeSort = (setter) => (col, defDir) =>
  setter(prev => (prev.key === col ? { key: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: defDir }));

// En-tête de colonne cliquable (▲/▼). align 'l' → tri ascendant par défaut, 'r' → descendant.
function SortTh({ label, col, sort, onSort, align = 'r' }) {
  const active = sort.key === col;
  const ind = active ? (sort.dir === 'asc' ? '▲' : '▼') : '';
  return (
    <th className={`th-${align} rev-th-sort${active ? ' rev-th-sort--active' : ''}`}
        onClick={() => onSort(col, align === 'l' ? 'asc' : 'desc')}>
      {label}{ind && <span className="rev-sort-ind"> {ind}</span>}
    </th>
  );
}

// ── Bridge price / volume / mix sur un set de segments {key, h, r} ──────────────
export function computeBridge(cy, py) {
  const pyByKey = new Map(py.map(s => [s.key, s]));
  const cyByKey = new Map(cy.map(s => [s.key, s]));
  const matched = cy.filter(s => pyByKey.has(s.key)).map(s => [s, pyByKey.get(s.key)]);
  const Hcy = matched.reduce((a, [c]) => a + c.h, 0);
  const Hpy = matched.reduce((a, [, p]) => a + p.h, 0);
  const Rpy = matched.reduce((a, [, p]) => a + p.r, 0);
  const avgRatePy = Hpy > 0 ? Rpy / Hpy : 0;
  let volume = (Hcy - Hpy) * avgRatePy, price = 0, mix = 0;
  for (const [c, p] of matched) {
    const rPy = p.h > 0 ? p.r / p.h : 0;
    const rCy = c.h > 0 ? c.r / c.h : 0;
    price += c.h * (rCy - rPy);
    mix += ((Hcy > 0 ? c.h / Hcy : 0) - (Hpy > 0 ? p.h / Hpy : 0)) * Hcy * (rPy - avgRatePy);
  }
  const scopeNew = cy.filter(s => !pyByKey.has(s.key)).reduce((a, s) => a + s.r, 0);
  const scopeOut = -py.filter(s => !cyByKey.has(s.key)).reduce((a, s) => a + s.r, 0);
  const rPyTot = py.reduce((a, s) => a + s.r, 0);
  const rCyTot = cy.reduce((a, s) => a + s.r, 0);
  return { volume, price, mix, scope: scopeNew + scopeOut, total: rCyTot - rPyTot, rPy: rPyTot, rCy: rCyTot };
}

// ── Réalisé vs Std price (CY) — décomposition prix / mix, par employé ───────────
// Anchor "Std @ avg" = ΣH × moyenne(std) → +Mix → +Price → Billed.
//   Mix   = Σ h_i × (std_i − moyenne_std)  → bascule des heures vers profils premium/discount
//   Price = Σ h_i × (eff_i − std_i)        → réalisation vs prix standard de chacun
// Calculé par employé puis agrégé. N'inclut que les employés avec std saisi & billable > 0.
export function computeStdBridge(persons) {
  const v = persons.filter(p => p.billable > 0 && p.hasStd);
  if (!v.length) return null;
  const H = v.reduce((a, p) => a + p.billable, 0);
  const billed = v.reduce((a, p) => a + p.billed, 0);
  const stdRev = v.reduce((a, p) => a + p.stdPrice * p.billable, 0);
  const meanStd = v.reduce((a, p) => a + p.stdPrice, 0) / v.length;
  const anchor = meanStd * H;
  return { anchor, mix: stdRev - anchor, price: billed - stdRev, stdRev, billed, n: v.length };
}

// Construit les personnes (hors pools overhead) depuis les lignes CHR d'une année.
export function buildPersons(detail, overheadIds) {
  const rows = (detail && detail.rows) || [];
  return rows
    .filter(r => !(r.cost_pool_id && overheadIds.has(r.cost_pool_id)))
    .map(r => {
      const billable = Number(r.heures_tf) || 0;
      // Prix standard = source UNIQUE (Input Analytics → standard_price). Lecture seule ici.
      const stdPrice = r.standard_price != null ? Number(r.standard_price) : 0;
      return {
        key: r.person_id,
        name: `${r.last_name || ''} ${r.first_name || ''}`.trim() || r.person_name || '—',
        last: (r.last_name || '').trim(),
        first: (r.first_name || '').trim(),
        poolId: r.cost_pool_id || UNASSIGNED,
        poolName: r.cost_pool_name || 'Unassigned',
        fte: r.fte != null ? Number(r.fte) : (Number(r.fte_pct) || 0) / 100,
        tr: Number(r.heures_prod) || 0,           // capacité pratique (TR)
        ot: Number(r.heures_contractuelles) || 0, // heures contractuelles (OT)
        billable,
        stdPrice,
        hasStd: r.standard_price != null,
        stdRev: stdPrice * billable,
        billed: r.billed_amount != null ? Number(r.billed_amount) : 0,
        hasBilled: r.billed_amount != null,
      };
    });
}

export function aggregateByPool(persons) {
  const m = new Map();
  for (const p of persons) {
    const g = m.get(p.poolId) || { key: p.poolId, name: p.poolName, h: 0, r: 0, stdRev: 0, tr: 0, capFte: 0 };
    g.h += p.billable; g.r += p.billed; g.stdRev += p.stdRev; g.tr += p.tr; g.capFte += (p.capFte || 0);
    m.set(p.poolId, g);
  }
  return [...m.values()];
}

// ── Cellule de saisie générique (bleu user-input), contrôlée par le parent ──────
// activeKey = clé de la cellule actuellement en édition ; nextKey = cellule du
// dessous (même colonne) pour la navigation Enter. col = 'billed' | 'std'.
function EditCell({ id, col, value, onSave, fmt = chf, activeKey, setActiveKey, nextKey }) {
  const p = useP();
  const cellKey = `${col}:${id}`;
  const editing = activeKey === cellKey;
  const [draft, setDraft] = useState('');
  const ref = useRef();
  const skipBlur = useRef(false);
  useEffect(() => {
    if (editing) { setDraft(value != null ? String(value) : ''); if (ref.current) { ref.current.focus(); ref.current.select(); } }
  }, [editing]); // eslint-disable-line
  function commit(moveNext) {
    const raw = (draft + '').trim().replace(/[\s']/g, '').replace(',', '.');
    if (raw === '') onSave(id, null);
    else { const v = parseFloat(raw); if (!isNaN(v) && v >= 0) onSave(id, v); }
    setActiveKey(moveNext ? (nextKey || null) : null);
  }
  if (editing) {
    return (
      <input ref={ref} className="rev-input" type="text" inputMode="decimal" value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (skipBlur.current) { skipBlur.current = false; return; } commit(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlur.current = true; commit(true); }
          else if (e.key === 'Escape') { e.preventDefault(); skipBlur.current = true; setActiveKey(null); }
        }} />
    );
  }
  return (
    <span className="rev-billed-cell" title={p('Cliquer pour saisir')}
      onClick={() => setActiveKey(cellKey)}>
      {value != null ? fmt(value) : <span className="rev-muted">—</span>}
    </span>
  );
}

// ── Bridge card ────────────────────────────────────────────────────────────────
function BridgeRow({ label, b }) {
  const steps = [
    { k: 'Volume', v: b.volume },
    { k: 'Price',  v: b.price },
    { k: 'Mix',    v: b.mix },
  ];
  if (Math.abs(b.scope) > 0.5) steps.push({ k: 'Scope', v: b.scope });
  return (
    <div className="rev-bridge-row">
      {label && <span className="rev-bridge-by">{label}</span>}
      <span className="rev-bridge-step rev-bridge-anchor">
        <span className="rev-bridge-k">Revenue PY</span>
        <span className="rev-bridge-v">{chf(b.rPy)}</span>
      </span>
      {steps.map(s => (
        <span key={s.k} className="rev-bridge-step">
          <span className="rev-bridge-k">{s.k}</span>
          <span className={`rev-bridge-v ${sign(s.v)}`}>{signed(s.v)}</span>
        </span>
      ))}
      <span className="rev-bridge-step rev-bridge-anchor">
        <span className="rev-bridge-k">Revenue CY</span>
        <span className="rev-bridge-v">{chf(b.rCy)}</span>
      </span>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function RevenuesPage({ activeTenant, addToast, year: yearProp, embedded = false }) {
  const p = useP();
  const tenantId = activeTenant?.id;
  const year = yearProp ?? new Date().getFullYear();
  const py = year - 1;

  const [cy, setCy] = useState(null);
  const [pyData, setPyData] = useState(null);
  const [costPools, setCostPools] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tri des tables + filtre pool de la table « By employee ».
  const [poolSort, setPoolSort]     = usePersistentValue(`tdabc_rev_pool_sort_${tenantId}`, { key: 'billedCy', dir: 'desc' });
  const [personSort, setPersonSort] = usePersistentValue(`tdabc_rev_person_sort_${tenantId}`, { key: 'billedCy', dir: 'desc' });
  const [personPool, setPersonPool] = usePersistentValue(`tdabc_rev_person_pool_${tenantId}`, null);
  const [editKey, setEditKey] = useState(null);   // cellule en édition : `${col}:${person_id}`
  const onPoolSort   = makeSort(setPoolSort);
  const onPersonSort = makeSort(setPersonSort);

  function reload() {
    if (!tenantId) { setCy(null); setPyData(null); setCostPools([]); return; }
    setLoading(true);
    Promise.all([
      getChrYearly(tenantId, year).then(r => { if (r?.error) throw new Error(r.error); return r.data || r; }),
      getChrYearly(tenantId, py).then(r => (r?.error ? { rows: [] } : (r.data || r))).catch(() => ({ rows: [] })),
      listCostPools(tenantId),
    ])
      .then(([c, p, cp]) => { setCy(c); setPyData(p); setCostPools((cp && cp.data) || cp || []); })
      .catch(e => addToast(e?.message || 'Error loading revenues', 'error'))
      .finally(() => setLoading(false));
  }
  useEffect(reload, [tenantId, year]); // eslint-disable-line

  // Mise à jour optimiste locale (pas de reload → pas de flash/loading ni de saut de scroll).
  function patchLocal(personId, patch) {
    setCy(prev => (prev && prev.rows
      ? { ...prev, rows: prev.rows.map(r => (r.person_id === personId ? { ...r, ...patch } : r)) }
      : prev));
  }
  async function saveBilled(personId, value) {
    patchLocal(personId, { billed_amount: value });
    try { await upsertChrYearlyInput(tenantId, year, personId, { billed_amount: value }); }
    catch (e) { addToast(e?.message || 'Save error', 'error'); reload(); }
  }
  async function saveStd(personId, value) {
    patchLocal(personId, { std_price: value });
    try { await upsertChrYearlyInput(tenantId, year, personId, { std_price: value }); }
    catch (e) { addToast(e?.message || 'Save error', 'error'); reload(); }
  }

  const model = useMemo(() => {
    const overheadIds = new Set((costPools || []).filter(p => p.is_overhead_pool).map(p => p.id));
    const pCy = buildPersons(cy, overheadIds);
    const pPy = buildPersons(pyData, overheadIds);
    // Capacity FTE = TR ÷ heures pour 100 % FTE (Σ OT ÷ Σ FTE). Attaché par personne avant agrégation.
    const attachCapFte = (persons) => {
      const ot = persons.reduce((a, p) => a + (p.ot || 0), 0);
      const fte = persons.reduce((a, p) => a + (p.fte || 0), 0);
      const hpf = fte > 0 ? ot / fte : 0;
      persons.forEach(p => { p.capFte = hpf > 0 ? p.tr / hpf : 0; });
      return hpf;
    };
    attachCapFte(pCy);
    attachCapFte(pPy);
    const poolsCy = aggregateByPool(pCy);
    const poolsPy = aggregateByPool(pPy);
    // index PY par personne / pool pour le tableau comparatif
    const pyPersonByKey = new Map(pPy.map(p => [p.key, p]));
    const pyPoolByKey = new Map(poolsPy.map(p => [p.key, p]));
    return {
      pCy, pPy, poolsCy, poolsPy, pyPersonByKey, pyPoolByKey,
      bridgePool: computeBridge(poolsCy.map(p => ({ key: p.key, h: p.h, r: p.r })), poolsPy.map(p => ({ key: p.key, h: p.h, r: p.r }))),
      bridgePerson: computeBridge(pCy.map(p => ({ key: p.key, h: p.billable, r: p.billed })), pPy.map(p => ({ key: p.key, h: p.billable, r: p.billed }))),
      stdBridge: computeStdBridge(pCy),
    };
  }, [cy, pyData, costPools]);

  const wrapCls = `page-table-layout rev-layout is-fluid is-compact${embedded ? ' rev-layout--embedded' : ''}`;
  if (!activeTenant) {
    return <main className={wrapCls}><div className="page-table-empty">Select a company.</div></main>;
  }
  if (loading) {
    return <main className={wrapCls}><p className="page-table-loading">Loading…</p></main>;
  }

  const { pCy, poolsCy, pyPersonByKey, pyPoolByKey, bridgePool, bridgePerson, stdBridge } = model;

  const tot = (arr, k) => arr.reduce((s, x) => s + (Number(x[k]) || 0), 0);
  const rCy = tot(poolsCy, 'r'), hCy = tot(poolsCy, 'h');
  const rPy = bridgePool.rPy, hPy = tot(model.poolsPy, 'h');
  const effCy = hCy > 0 ? rCy / hCy : null;
  const effPy = hPy > 0 ? rPy / hPy : null;
  const dRevPct = rPy ? (rCy - rPy) / rPy : null;
  const capFteCy = model.pCy.reduce((a, p) => a + (p.capFte || 0), 0);
  const capFtePy = model.pPy.reduce((a, p) => a + (p.capFte || 0), 0);
  const caCapFteCy = capFteCy > 0 ? rCy / capFteCy : null;
  const caCapFtePy = capFtePy > 0 ? rPy / capFtePy : null;

  // ── Lignes enrichies (valeurs PY + dérivées) pour tri/filtre ──
  const poolTableRows = poolsCy.map(p => {
    const prev = pyPoolByKey.get(p.key) || { h: 0, r: 0 };
    return {
      ...p,
      prevH: prev.h, prevR: prev.r,
      eCy: p.h > 0 ? p.r / p.h : null,
      ePy: prev.h > 0 ? prev.r / prev.h : null,
      d: prev.r ? (p.r - prev.r) / prev.r : null,
      std: p.h > 0 ? p.stdRev / p.h : null,
    };
  });
  const poolAccessors = {
    name: r => r.name, capFte: r => r.capFte, std: r => r.std,
    billablePy: r => r.prevH, billableCy: r => r.h,
    billedPy: r => r.prevR, billedCy: r => r.r,
    delta: r => r.d, effPy: r => r.ePy, effCy: r => r.eCy,
  };
  const poolRows = sortRows(poolTableRows, poolSort, poolAccessors);

  const personTableRows = pCy.map(p => {
    const prev = pyPersonByKey.get(p.key) || { billable: 0, billed: 0, hasBilled: false };
    return {
      ...p,
      prevBillable: prev.billable, prevBilled: prev.billed, prevHasBilled: prev.hasBilled,
      eCy: p.billable > 0 ? p.billed / p.billable : null,
      ePy: prev.billable > 0 ? prev.billed / prev.billable : null,
      sortName: `${p.last} ${p.first}`.trim().toLowerCase(),   // tri nom puis prénom
    };
  });
  const personAccessors = {
    name: r => r.sortName, pool: r => r.poolName, capFte: r => r.capFte,
    std: r => (r.hasStd ? r.stdPrice : null),
    billablePy: r => r.prevBillable, billableCy: r => r.billable,
    billedPy: r => (r.prevHasBilled ? r.prevBilled : null),
    billedCy: r => (r.hasBilled ? r.billed : null),
    effPy: r => r.ePy, effCy: r => r.eCy,
  };
  const personPools = [...new Set(pCy.map(p => p.poolName || 'Unassigned'))].sort((a, b) => a.localeCompare(b, 'fr'));
  const personRowsFiltered = personPool
    ? personTableRows.filter(p => (p.poolName || 'Unassigned') === personPool)
    : personTableRows;
  const personRows = sortRows(personRowsFiltered, personSort, personAccessors);
  // Totaux / moyennes de la table By employee (sur les lignes affichées/filtrées).
  const pFoot = personRows.reduce((a, p) => ({
    capFte: a.capFte + (p.capFte || 0),
    stdRev: a.stdRev + (p.stdRev || 0),
    billable: a.billable + (p.billable || 0),
    prevBillable: a.prevBillable + (p.prevBillable || 0),
    billed: a.billed + (p.billed || 0),
    prevBilled: a.prevBilled + (p.prevBilled || 0),
  }), { capFte: 0, stdRev: 0, billable: 0, prevBillable: 0, billed: 0, prevBilled: 0 });
  const pFootStd = pFoot.billable > 0 ? pFoot.stdRev / pFoot.billable : null;
  const pFootEffCy = pFoot.billable > 0 ? pFoot.billed / pFoot.billable : null;
  const pFootEffPy = pFoot.prevBillable > 0 ? pFoot.prevBilled / pFoot.prevBillable : null;

  return (
    <main className={wrapCls}>
      {!embedded && (
        <PageHeader
          title="Revenues"
        />
      )}

      {poolsCy.length === 0 ? (
        <div className="page-table-empty">No data for {year}.</div>
      ) : (
        <>
          {/* ── Synthèse unique : KPIs + analyses (bridges) ── */}
          <section className="rev-synth">
            <div className="rev-synth__head">Revenues · {year} · {pCy.length} persons</div>
            <div className="rev-synth__row">
              <div className="rev-synth__item">
                <span className="rev-synth__label">Total billed</span>
                <span className="rev-synth__value">{chf(rCy)} <small>CHF</small></span>
                <span className={`rev-synth__sub ${sign(rCy - rPy)}`}>{signed(rCy - rPy)} vs {py} · {pct(dRevPct)}</span>
              </div>
              <div className="rev-synth__item">
                <span className="rev-synth__label">Billable hours</span>
                <span className="rev-synth__value">{chf(hCy)} <small>h</small></span>
                <span className={`rev-synth__sub ${sign(hCy - hPy)}`}>{signed(hCy - hPy)} vs {py}</span>
              </div>
              <div className="rev-synth__item">
                <span className="rev-synth__label">Effective rate</span>
                <span className="rev-synth__value">{rate(effCy)} <small>CHF/h</small></span>
                <span className={`rev-synth__sub ${sign((effCy || 0) - (effPy || 0))}`}>
                  {effPy != null ? `${signed(effCy - effPy)} vs ${py}` : `prior n/a`}
                </span>
              </div>
              <div className="rev-synth__item">
                <span className="rev-synth__label">Capacity FTE</span>
                <span className="rev-synth__value">{fmtFte(capFteCy)}</span>
                <span className={`rev-synth__sub ${sign(capFteCy - capFtePy)}`}>
                  {capFtePy > 0 ? `${capFteCy - capFtePy > 0 ? '+' : ''}${fmtFte(capFteCy - capFtePy)} vs ${py}` : p('TR ÷ h pour 100 % FTE')}
                </span>
              </div>
              <div className="rev-synth__item">
                <span className="rev-synth__label">Revenues / cap.FTE</span>
                <span className="rev-synth__value">{chf(caCapFteCy)} <small>CHF</small></span>
                <span className={`rev-synth__sub ${sign((caCapFteCy || 0) - (caCapFtePy || 0))}`}>
                  {caCapFtePy != null ? `${signed(caCapFteCy - caCapFtePy)} vs ${py}` : p('facturé ÷ capacity FTE')}
                </span>
              </div>
            </div>

            {(bridgePerson.rPy > 0 || stdBridge) && (
              <div className="rev-synth__analysis">
                <div className="rev-analysis-cols">
                  {bridgePerson.rPy > 0 && (
                    <div className="rev-analysis-block rev-analysis-block--py">
                      <div className="rev-bridge-sub">Prior year analysis · {py} → {year}</div>
                      <BridgeRow b={bridgePerson} />
                    </div>
                  )}
                  {stdBridge && (
                    <div className="rev-analysis-block">
                      <div className="rev-bridge-sub">Standard comparison · {year} ({stdBridge.n} {p('avec std')})</div>
                      <div className="rev-bridge-row">
                        <span className="rev-bridge-step rev-bridge-anchor">
                          <span className="rev-bridge-k">Rev @ STD</span>
                          <span className="rev-bridge-v">{chf(stdBridge.stdRev)}</span>
                        </span>
                        <span className="rev-bridge-step">
                          <span className="rev-bridge-k">Price</span>
                          <span className={`rev-bridge-v ${sign(stdBridge.price)}`}>{signed(stdBridge.price)}</span>
                        </span>
                        <span className="rev-bridge-step rev-bridge-anchor">
                          <span className="rev-bridge-k">Billed {year}</span>
                          <span className="rev-bridge-v">{chf(stdBridge.billed)}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="rev-bridge-note">
                  {p('Tout calculé par employé puis agrégé. · ')}
                  <strong>Bridge PY→CY</strong>
                  {p(' : Volume = Δh × tarif PY · Price = Σh_cy × Δtarif · Mix = bascule entre employés · Scope = arrivées − départs. · ')}
                  <strong>{p('Réalisé vs Std')}</strong>
                  {p(' : Rev @ STD = Σ std × billable · Price = Σh × (tarif effectif − std) = réalisation vs prix standard. Somme = Billed.')}
                </p>
              </div>
            )}
          </section>

          {/* ── Table par pool ── */}
          <section className="rev-card">
            <p className="rev-card-title">By pool</p>
            <div className="rev-table-wrap">
              <table className="rev-table">
                <thead>
                  <tr>
                    <SortTh label="Pool"               col="name"       align="l" sort={poolSort} onSort={onPoolSort} />
                    <SortTh label="Capacity FTE"       col="capFte"     sort={poolSort} onSort={onPoolSort} />
                    <SortTh label="Std price"          col="std"        sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Billable h ${py}`}   col="billablePy" sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Billable h ${year}`} col="billableCy" sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Billed ${py}`}       col="billedPy"   sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Billed ${year}`}     col="billedCy"   sort={poolSort} onSort={onPoolSort} />
                    <SortTh label="Δ %"                col="delta"      sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Eff. rate ${py}`}    col="effPy"      sort={poolSort} onSort={onPoolSort} />
                    <SortTh label={`Eff. rate ${year}`}  col="effCy"      sort={poolSort} onSort={onPoolSort} />
                  </tr>
                </thead>
                <tbody>
                  {poolRows.map(p => (
                    <tr key={p.key}>
                      <td className="rev-td-name">{p.name}</td>
                      <td className="rev-td-num">{fmtFte(p.capFte)}</td>
                      <td className="rev-td-num rev-muted">{rate(p.std)}</td>
                      <td className="rev-td-num rev-muted">{chf(p.prevH)}</td>
                      <td className="rev-td-num">{chf(p.h)}</td>
                      <td className="rev-td-num rev-muted">{chf(p.prevR)}</td>
                      <td className="rev-td-num"><strong>{chf(p.r)}</strong></td>
                      <td className={`rev-td-num ${sign(p.d)}`}>{pct(p.d)}</td>
                      <td className="rev-td-num rev-muted">{rate(p.ePy)}</td>
                      <td className="rev-td-num rev-accent">{rate(p.eCy)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="rev-tr-foot">
                    <td className="rev-td-name">TOTAL</td>
                    <td className="rev-td-num"><strong>{fmtFte(capFteCy)}</strong></td>
                    <td className="rev-td-num rev-muted">{rate(hCy > 0 ? poolsCy.reduce((s, x) => s + x.stdRev, 0) / hCy : null)}</td>
                    <td className="rev-td-num rev-muted">{chf(hPy)}</td>
                    <td className="rev-td-num">{chf(hCy)}</td>
                    <td className="rev-td-num rev-muted">{chf(rPy)}</td>
                    <td className="rev-td-num"><strong>{chf(rCy)}</strong></td>
                    <td className={`rev-td-num ${sign(dRevPct)}`}>{pct(dRevPct)}</td>
                    <td className="rev-td-num rev-muted">{rate(effPy)}</td>
                    <td className="rev-td-num rev-accent">{rate(effCy)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* ── Table par employé (saisie du facturé CY) ── */}
          <section className="rev-card">
            <p className="rev-card-title">By employee <small>(billed {year}{p(' = saisie · clic pour éditer)')}</small></p>
            {personPools.length > 1 && (
              <div className="rev-pool-filter">
                <button
                  className={`rev-pool-btn${!personPool ? ' rev-pool-btn--active' : ''}`}
                  onClick={() => setPersonPool(null)}
                >
                  {p('Tous')} <span className="rev-pool-count">{pCy.length}</span>
                </button>
                {personPools.map(name => (
                  <button
                    key={name}
                    className={`rev-pool-btn${personPool === name ? ' rev-pool-btn--active' : ''}`}
                    onClick={() => setPersonPool(prev => prev === name ? null : name)}
                  >
                    {name}
                    <span className="rev-pool-count">{pCy.filter(p => (p.poolName || 'Unassigned') === name).length}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="rev-table-wrap">
              <table className="rev-table">
                <thead>
                  <tr>
                    <SortTh label="Person"             col="name"       align="l" sort={personSort} onSort={onPersonSort} />
                    <SortTh label="Pool"               col="pool"       align="l" sort={personSort} onSort={onPersonSort} />
                    <SortTh label="Capacity FTE"       col="capFte"     sort={personSort} onSort={onPersonSort} />
                    <SortTh label="Std price"          col="std"        sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Billable h ${py}`}   col="billablePy" sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Billable h ${year}`} col="billableCy" sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Billed ${py}`}       col="billedPy"   sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Billed ${year}`}     col="billedCy"   sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Eff. rate ${py}`}    col="effPy"      sort={personSort} onSort={onPersonSort} />
                    <SortTh label={`Eff. rate ${year}`}  col="effCy"      sort={personSort} onSort={onPersonSort} />
                  </tr>
                </thead>
                <tbody>
                  {personRows.map((row, idx) => {
                    const nextId = personRows[idx + 1] ? personRows[idx + 1].key : null;
                    return (
                    <tr key={row.key}>
                      <td className="rev-td-name">{row.name}</td>
                      <td className="rev-td-name rev-muted">{row.poolName}</td>
                      <td className="rev-td-num">{fmtFte(row.capFte)}</td>
                      <td className="rev-td-num" title={p('Prix standard — saisi dans Input Analytics (lecture seule ici)')}>
                        {row.hasStd ? rate(row.stdPrice) : '—'}
                      </td>
                      <td className="rev-td-num rev-muted">{chf(row.prevBillable)}</td>
                      <td className="rev-td-num">{chf(row.billable)}</td>
                      <td className="rev-td-num rev-muted">{row.prevHasBilled ? chf(row.prevBilled) : <span className="rev-muted">—</span>}</td>
                      <td className="rev-td-num rev-td-edit">
                        <EditCell id={row.key} col="billed" value={row.hasBilled ? row.billed : null} onSave={saveBilled}
                                  activeKey={editKey} setActiveKey={setEditKey} nextKey={nextId ? `billed:${nextId}` : null} />
                      </td>
                      <td className="rev-td-num rev-muted">{rate(row.ePy)}</td>
                      <td className="rev-td-num rev-accent">{rate(row.eCy)}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="rev-tr-foot">
                    <td className="rev-td-name" colSpan={2}>TOTAL / AVERAGE · {personRows.length} persons</td>
                    <td className="rev-td-num"><strong>{fmtFte(pFoot.capFte)}</strong></td>
                    <td className="rev-td-num rev-muted">{rate(pFootStd)}</td>
                    <td className="rev-td-num rev-muted">{chf(pFoot.prevBillable)}</td>
                    <td className="rev-td-num">{chf(pFoot.billable)}</td>
                    <td className="rev-td-num rev-muted">{chf(pFoot.prevBilled)}</td>
                    <td className="rev-td-num"><strong>{chf(pFoot.billed)}</strong></td>
                    <td className="rev-td-num rev-muted">{rate(pFootEffPy)}</td>
                    <td className="rev-td-num rev-accent">{rate(pFootEffCy)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
